import { ORPCError } from '@orpc/client';

/**
 * Turns a failed call into something the parent can read.
 *
 * The backend returns stable machine codes and never prose (AGENTS.md §1.3), so
 * translating them is this application's job. The list below is the set this
 * application has words for; a test asserts that every entry exists in every
 * locale, which is what stops a new endpoint's error reaching a parent as a raw
 * `CHILD_LIMIT_REACHED`.
 *
 * Anything unrecognised becomes `UNEXPECTED`. A parent shown a code they cannot
 * act on has been told nothing, and the alternative — showing the code itself —
 * is worse, because it looks like the product is broken in a way they caused.
 */
export const TRANSLATED_ERROR_CODES = [
  'EMAIL_ALREADY_REGISTERED',
  'INVALID_CREDENTIALS',
  'INVALID_REFRESH_TOKEN',
  'CHILD_NOT_FOUND',
  'CHILD_LIMIT_REACHED',
  'CHILD_NOT_ASSIGNED',
  'PAIRING_NOT_FOUND',
  'PAIRING_ALREADY_CLAIMED',
  'DEVICE_NOT_FOUND',
  'DEVICE_LIMIT_REACHED',
  'DEVICE_UNPAIRED',
  'CONTENT_NOT_FOUND',
  'COLLECTION_NOT_FOUND',
  'PROVIDER_UNAVAILABLE',
  'INVALID_YOUTUBE_URL',
  'VIDEO_NOT_FOUND',
  'NOT_EMBEDDABLE',
  'SECONDS_REQUIRED',
  'CONTENT_REQUIRED',
  'CONTENT_NOT_APPROVED',
  'INVALID_SOURCE_URL',
  'SOURCE_NOT_FOUND',
  'IMPORT_UNAVAILABLE',
  'IMPORT_JOB_NOT_FOUND',
  'UNAUTHORIZED',
  'OFFLINE',
  'UNEXPECTED',
] as const;

export type TranslatedErrorCode = (typeof TRANSLATED_ERROR_CODES)[number];

const isTranslated = (code: string): code is TranslatedErrorCode =>
  (TRANSLATED_ERROR_CODES as readonly string[]).includes(code);

export const toErrorCode = (error: unknown): TranslatedErrorCode => {
  if (error instanceof ORPCError) {
    return isTranslated(error.code) ? error.code : 'UNEXPECTED';
  }

  // A fetch that never reached the server. Worth saying out loud, because the
  // parent's next move is different: check the connection, not the password.
  if (error instanceof TypeError) {
    return 'OFFLINE';
  }

  return 'UNEXPECTED';
};

/** The translation key for a failed call. */
export const toErrorKey = (error: unknown): string => `error.${toErrorCode(error)}`;
